import React, { useEffect, useState } from "react";
import { Sparkles, AlertCircle, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

/**
 * Trial / plan status banner shown in AppShell topbar.
 *
 * • Trial → shows days remaining + scans used + clients used + Upgrade CTA.
 * • Past due → red banner.
 * • Cancel scheduled → amber banner.
 * • Otherwise hidden.
 */
export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/api/billing/subscription"); setSub(data); } catch { /* ignore */ }
    })();
  }, []);

  if (!sub || dismissed) return null;

  const isTrial = sub.subscription_status === "trialing" || sub.plan?.key === "trial";
  const isPastDue = sub.subscription_status === "past_due";
  const isCancelling = sub.cancel_at_period_end;

  let daysLeft = null;
  if (sub.trial_ends_at) {
    const ms = new Date(sub.trial_ends_at).getTime() - Date.now();
    daysLeft = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }

  if (!isTrial && !isPastDue && !isCancelling) return null;

  const usage = sub.usage || {};
  const plan = sub.plan || {};

  if (isPastDue) {
    return (
      <div className="bg-rose-50 border-b border-rose-200 px-5 lg:px-8 py-2.5 flex items-center gap-3 text-rose-900 t-body-sm" data-testid="banner-past-due">
        <AlertCircle size={16} />
        <span className="flex-1">Your last payment failed. Please update your card to keep your subscription active.</span>
        <Link to="/app/settings#billing" className="font-semibold underline">Update payment</Link>
      </div>
    );
  }

  if (isCancelling && !isTrial) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-5 lg:px-8 py-2.5 flex items-center gap-3 text-amber-900 t-body-sm" data-testid="banner-cancelling">
        <AlertCircle size={16} />
        <span className="flex-1">Subscription scheduled to cancel at the end of this period.</span>
        <Link to="/app/settings#billing" className="font-semibold underline">Reactivate</Link>
        <button onClick={() => setDismissed(true)} className="text-amber-700 hover:text-amber-900"><X size={14} /></button>
      </div>
    );
  }

  // Trial
  return (
    <div className="bg-indigo2/10 border-b border-indigo2/20 px-5 lg:px-8 py-2.5 flex items-center gap-3 text-ink t-body-sm flex-wrap" data-testid="banner-trial">
      <Sparkles size={16} className="text-indigo2" />
      <span className="font-medium">
        {daysLeft !== null ? `Trial: ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left` : "Free trial active"}
      </span>
      <span className="text-slate2 hidden sm:inline">·</span>
      <span className="text-slate2 hidden sm:inline">
        {usage.scans_this_month || 0} / {plan.scans_per_month ?? "∞"} scans
      </span>
      <span className="text-slate2 hidden md:inline">·</span>
      <span className="text-slate2 hidden md:inline">
        {usage.clients_active || 0} / {plan.clients_limit ?? "∞"} clients
      </span>
      <Link to="/app/settings#billing" className="ml-auto btn btn-primary btn-sm" data-testid="banner-trial-upgrade">Upgrade plan</Link>
    </div>
  );
}
